export type StandardRating =
  | 'EXCEEDING'
  | 'CONFORMANCE'
  | 'MINOR_NON_CONFORMANCE'
  | 'MAJOR_NON_CONFORMANCE';

export interface AuditStandardAssessmentInput {
  standardNumber: number;
  rating: StandardRating;
}

export interface AuditEvidenceInput {
  standardNumber: number | null;
}

export interface AuditActionItemInput {
  status: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE';
}

export interface AuditScheduleAdherenceInput {
  scheduledDate: string;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  completedDate?: string | null;
}

export interface AuditReadinessInput {
  assessments: AuditStandardAssessmentInput[];
  evidence: AuditEvidenceInput[];
  actionItems: AuditActionItemInput[];
  scheduleHistory: AuditScheduleAdherenceInput[];
}

export interface AuditReadinessResult {
  score: number;
  components: {
    standardRatings: number;
    evidenceCoverage: number;
    actionClosure: number;
    scheduleAdherence: number;
  };
}

const RATING_WEIGHTS: Record<StandardRating, number> = {
  EXCEEDING: 100,
  CONFORMANCE: 85,
  MINOR_NON_CONFORMANCE: 50,
  MAJOR_NON_CONFORMANCE: 0,
};

const COMPONENT_WEIGHTS = {
  standardRatings: 0.4,
  evidenceCoverage: 0.25,
  actionClosure: 0.25,
  scheduleAdherence: 0.1,
} as const;

export function ratingToScore(rating: StandardRating): number {
  return RATING_WEIGHTS[rating];
}

function latestRatingPerStandard(assessments: AuditStandardAssessmentInput[]): Map<number, number> {
  const latest = new Map<number, number>();
  for (const row of assessments) {
    if (row.standardNumber < 1 || row.standardNumber > 7) continue;
    latest.set(row.standardNumber, ratingToScore(row.rating));
  }
  return latest;
}

function computeStandardRatingsScore(assessments: AuditStandardAssessmentInput[]): number {
  const latest = latestRatingPerStandard(assessments);
  if (latest.size === 0) return 0;
  const scoreSum = Array.from(latest.values()).reduce((sum, value) => sum + value, 0);
  return scoreSum / latest.size;
}

function computeEvidenceCoverageScore(
  assessments: AuditStandardAssessmentInput[],
  evidence: AuditEvidenceInput[],
): number {
  const standardsWithAssessments = new Set(
    assessments.map((row) => row.standardNumber).filter((n) => n >= 1 && n <= 7),
  );
  if (standardsWithAssessments.size === 0) {
    const covered = new Set(
      evidence
        .map((row) => row.standardNumber)
        .filter((n): n is number => n != null && n >= 1 && n <= 7),
    );
    return covered.size === 0 ? 0 : (covered.size / 7) * 100;
  }

  const standardsWithEvidence = new Set<number>();
  for (const row of evidence) {
    if (row.standardNumber != null && row.standardNumber >= 1 && row.standardNumber <= 7) {
      standardsWithEvidence.add(row.standardNumber);
    }
  }
  let covered = 0;
  for (const standard of standardsWithAssessments) {
    if (standardsWithEvidence.has(standard)) covered += 1;
  }
  return (covered / standardsWithAssessments.size) * 100;
}

function computeActionClosureScore(actionItems: AuditActionItemInput[]): number {
  const actionable = actionItems;
  if (actionable.length === 0) return 100;
  const closed = actionable.filter((row) => row.status === 'COMPLETED').length;
  const overdue = actionable.filter((row) => row.status === 'OVERDUE').length;
  const denominator = actionable.length;
  const raw = ((closed - overdue * 0.5) / denominator) * 100;
  return Math.max(0, Math.min(100, raw));
}

function computeScheduleAdherenceScore(history: AuditScheduleAdherenceInput[]): number {
  const relevant = history.filter((row) => row.status !== 'CANCELLED');
  if (relevant.length === 0) return 100;
  let onTime = 0;
  for (const row of relevant) {
    if (row.status === 'COMPLETED') {
      const completed = row.completedDate ? new Date(row.completedDate).getTime() : null;
      const scheduled = new Date(row.scheduledDate).getTime();
      if (completed != null && completed <= scheduled + 7 * 86_400_000) onTime += 1;
    } else if (row.status === 'IN_PROGRESS') {
      onTime += 1;
    } else if (row.status === 'SCHEDULED') {
      const scheduled = new Date(row.scheduledDate).getTime();
      if (scheduled >= Date.now()) onTime += 1;
    }
  }
  return (onTime / relevant.length) * 100;
}

export function computeAuditReadinessScore(input: AuditReadinessInput): AuditReadinessResult {
  if (
    input.assessments.length === 0 &&
    input.evidence.length === 0 &&
    input.actionItems.length === 0 &&
    input.scheduleHistory.length === 0
  ) {
    return {
      score: 0,
      components: {
        standardRatings: 0,
        evidenceCoverage: 0,
        actionClosure: 0,
        scheduleAdherence: 0,
      },
    };
  }

  const standardRatings = computeStandardRatingsScore(input.assessments);
  const evidenceCoverage = computeEvidenceCoverageScore(input.assessments, input.evidence);
  const actionClosure = computeActionClosureScore(input.actionItems);
  const scheduleAdherence = computeScheduleAdherenceScore(input.scheduleHistory);

  const score =
    standardRatings * COMPONENT_WEIGHTS.standardRatings +
    evidenceCoverage * COMPONENT_WEIGHTS.evidenceCoverage +
    actionClosure * COMPONENT_WEIGHTS.actionClosure +
    scheduleAdherence * COMPONENT_WEIGHTS.scheduleAdherence;

  return {
    score: Math.round(score * 10) / 10,
    components: {
      standardRatings: Math.round(standardRatings * 10) / 10,
      evidenceCoverage: Math.round(evidenceCoverage * 10) / 10,
      actionClosure: Math.round(actionClosure * 10) / 10,
      scheduleAdherence: Math.round(scheduleAdherence * 10) / 10,
    },
  };
}
